import React, { useState, useEffect } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useStudentAuth } from '../../hooks/useStudentAuth';
import { useStudentAccess } from '../../hooks/useStudentAccess';
import { useStudentTheme } from '../../hooks/useStudentTheme';
import { supabase } from '../../lib/supabase';
import {
  LayoutDashboard,
  Bell,
  User,
  LogOut,
  Menu,
  X,
  Award,
  ChevronLeft,
  ChevronRight,
  CreditCard,
  AlertTriangle,
  BookOpen,
  Sun,
  Moon,
} from 'lucide-react';

const ANNOUNCEMENTS_SEEN_EVENT = 'arabicroots:announcements-seen';

const navItems = [
  { to: '/dashboard',                label: 'Meine Kurse',            icon: LayoutDashboard, end: true },
  { to: '/dashboard/ankuendigungen', label: 'Ankündigungen',          icon: Bell,     badge: 'announcements' },
  { to: '/dashboard/lernapp',        label: 'Lernapp',                icon: BookOpen },
  { to: '/dashboard/zertifikate',    label: 'Zertifikate',            icon: Award },
  { to: '/dashboard/zahlungen',      label: 'Zahlungen & Rechnungen', icon: CreditCard, badge: 'payments' },
  { to: '/dashboard/profil',         label: 'Profil',                 icon: User },
];

const COLLAPSED_KEY = 'sidebar_collapsed';

const StudentDashboardLayout: React.FC = () => {
  const { user, profile, loading: authLoading, signOut } = useStudentAuth();
  const { isWarning, nextDueDate, hasAccess, hasPendingPayment, loading: accessLoading } = useStudentAccess({ user, profile, authLoading });
  const { isDark, toggleTheme } = useStudentTheme();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem(COLLAPSED_KEY) === 'true'; } catch { return false; }
  });
  const [unreadAnnouncements, setUnreadAnnouncements] = useState(0);
  const [openPayments, setOpenPayments] = useState(0);

  useEffect(() => {
    try { localStorage.setItem(COLLAPSED_KEY, String(collapsed)); } catch { /* ignore */ }
  }, [collapsed]);

  useEffect(() => {
    if (!profile) return;
    let active = true;
    const fetchUnread = async () => {
      const dbSince = profile.announcements_seen_at ?? '1970-01-01';
      let localSince = '1970-01-01';
      try { localSince = localStorage.getItem(`announcements_seen_${profile.id}`) ?? '1970-01-01'; } catch { /* ignore */ }
      const since = dbSince > localSince ? dbSince : localSince;

      // Klassen des Schülers holen
      const { data: memberships } = await supabase
        .from('portal_class_members')
        .select('class_id')
        .eq('student_id', profile.id)
        .eq('is_active', true);

      const classIds = (memberships ?? []).map((m: { class_id: string }) => m.class_id);

      // Ankündigungen zählen: nur eigene Klassen + globale (class_id = null)
      const { count } = await supabase
        .from('announcements')
        .select('id', { count: 'exact', head: true })
        .gt('created_at', since)
        .or(`class_id.is.null${classIds.length ? `,class_id.in.(${classIds.join(',')})` : ''}`);

      if (active) setUnreadAnnouncements(count ?? 0);
    };
    fetchUnread();
    const onSeen = () => setUnreadAnnouncements(0);
    window.addEventListener(ANNOUNCEMENTS_SEEN_EVENT, onSeen);
    return () => {
      active = false;
      window.removeEventListener(ANNOUNCEMENTS_SEEN_EVENT, onSeen);
    };
  }, [profile]);

  useEffect(() => {
    if (!profile || profile.role === 'admin') return;
    let active = true;
    const fetchPayments = async () => {
      const { data: plans } = await supabase
        .from('student_payment_plans')
        .select('id')
        .eq('student_id', profile.id)
        .eq('status', 'active');
      if (!active || !plans?.length) return;
      const { count } = await supabase
        .from('student_payments')
        .select('id', { count: 'exact', head: true })
        .in('plan_id', plans.map(p => p.id))
        .in('status', ['open', 'overdue']);
      if (active) setOpenPayments(count ?? 0);
    };
    fetchPayments();
    return () => { active = false; };
  }, [profile]);

  const handleLogout = async () => {
    try { localStorage.removeItem(COLLAPSED_KEY); } catch { /* ignore */ }
    await signOut();
    navigate('/login', { replace: true });
  };

  const fullName = profile
    ? `${profile.first_name} ${profile.last_name}`.trim() || profile.display_name || 'Schüler'
    : 'Schüler';

  const SidebarContent = ({ isMobile = false }: { isMobile?: boolean }) => (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className={`border-b border-gray-100 dark:border-neutral-800 flex items-center justify-center ${collapsed && !isMobile ? 'p-4 py-5' : 'p-6'}`}>
        <img
          src="/LogoHeader4.png"
          alt="Arabicroots"
          className={`object-contain dark:brightness-0 dark:invert ${collapsed && !isMobile ? 'w-10 h-10' : 'h-16 w-auto'}`}
          onError={e => (e.currentTarget.style.display = 'none')}
        />
      </div>

      {/* Nav */}
      <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
        {navItems.map(item => {
          const showBadge =
            (item.badge === 'announcements' && unreadAnnouncements > 0) ||
            (item.badge === 'payments' && openPayments > 0);
          const badgeCount = item.badge === 'announcements' ? unreadAnnouncements : openPayments;
          const isIconOnly = collapsed && !isMobile;
          return (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              onClick={() => setMobileOpen(false)}
              title={isIconOnly ? item.label : undefined}
              className={({ isActive }) =>
                `relative flex items-center gap-3 px-3 py-3 rounded-2xl text-sm font-medium transition-all duration-200 ${
                  isIconOnly ? 'justify-center' : ''
                } ${
                  isActive
                    ? 'bg-[#970000] text-white shadow-sm'
                    : 'text-gray-600 dark:text-neutral-300 hover:bg-gray-50 dark:hover:bg-neutral-800 hover:text-gray-900 dark:hover:text-neutral-100'
                }`
              }
            >
              <span className="relative shrink-0">
                <item.icon className="h-5 w-5" />
                {showBadge && isIconOnly && (
                  <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-[#970000] rounded-full ring-2 ring-white dark:ring-neutral-900" />
                )}
              </span>
              {(!collapsed || isMobile) && (
                <span className="flex-1 flex items-center justify-between gap-2">
                  {item.label}
                  {showBadge && (
                    <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-[11px] font-bold rounded-full bg-[#970000] text-white">
                      {badgeCount > 9 ? '9+' : badgeCount}
                    </span>
                  )}
                </span>
              )}
            </NavLink>
          );
        })}
      </nav>

      {/* User / Logout */}
      <div className="p-3 border-t border-[#eadfd6] dark:border-neutral-800">
        {(!collapsed || isMobile) ? (
          <>
            <div className="flex items-center gap-3 mb-3 px-2">
              <div className="w-9 h-9 rounded-xl bg-gray-100 dark:bg-neutral-800 flex items-center justify-center shrink-0">
                <User className="h-4 w-4 text-gray-500 dark:text-neutral-400" />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-gray-900 dark:text-neutral-100 truncate">{fullName}</p>
                <p className="text-xs text-gray-400 dark:text-neutral-500">Schüler</p>
              </div>
            </div>
            <button
              onClick={toggleTheme}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-2xl text-sm font-medium text-gray-500 dark:text-neutral-400 hover:bg-gray-50 dark:hover:bg-neutral-800 transition-colors duration-200"
            >
              {isDark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
              {isDark ? 'Heller Modus' : 'Dunkler Modus'}
            </button>
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-2xl text-sm font-medium text-gray-500 dark:text-neutral-400 hover:bg-gray-50 dark:hover:bg-neutral-800 transition-colors duration-200"
            >
              <LogOut className="h-4 w-4" />
              Abmelden
            </button>
          </>
        ) : (
          <>
            <button
              onClick={toggleTheme}
              title={isDark ? 'Heller Modus' : 'Dunkler Modus'}
              className="w-full flex items-center justify-center px-3 py-2.5 rounded-2xl text-sm font-medium text-gray-500 dark:text-neutral-400 hover:bg-gray-50 dark:hover:bg-neutral-800 transition-colors duration-200"
            >
              {isDark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </button>
            <button
              onClick={handleLogout}
              title="Abmelden"
              className="w-full flex items-center justify-center px-3 py-2.5 rounded-2xl text-sm font-medium text-gray-500 dark:text-neutral-400 hover:bg-gray-50 dark:hover:bg-neutral-800 transition-colors duration-200"
            >
              <LogOut className="h-4 w-4" />
            </button>
          </>
        )}
      </div>
    </div>
  );

  const sidebarWidth = collapsed ? 'w-[72px]' : 'w-72';

  return (
    <div className={isDark ? 'dark' : undefined} style={{ colorScheme: isDark ? 'dark' : 'light' }}>
    <div className="min-h-screen bg-gray-50 dark:bg-black flex">
      {/* Desktop sidebar */}
      <aside
        className={`hidden xl:flex xl:flex-col shrink-0 bg-white dark:bg-neutral-900 border-r border-gray-200 dark:border-neutral-800 fixed inset-y-0 left-0 z-20 transition-all duration-300 ${sidebarWidth}`}
      >
        <SidebarContent />
        {/* Collapse toggle */}
        <button
          onClick={() => setCollapsed(v => !v)}
          className="absolute -right-3 top-20 w-6 h-6 bg-white dark:bg-neutral-900 border border-gray-200 dark:border-neutral-800 rounded-full flex items-center justify-center shadow-sm hover:bg-gray-50 dark:hover:bg-neutral-800 transition-colors"
          title={collapsed ? 'Sidebar ausklappen' : 'Sidebar einklappen'}
        >
          {collapsed ? (
            <ChevronRight className="h-3.5 w-3.5 text-[#6b6b6b] dark:text-neutral-400" />
          ) : (
            <ChevronLeft className="h-3.5 w-3.5 text-[#6b6b6b] dark:text-neutral-400" />
          )}
        </button>
      </aside>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-30 xl:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Mobile drawer */}
      <aside
        className={`fixed inset-y-0 left-0 z-40 w-72 bg-white dark:bg-neutral-900 border-r border-gray-200 dark:border-neutral-800 transform transition-transform duration-300 xl:hidden ${
          mobileOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="absolute top-4 right-4">
          <button
            onClick={() => setMobileOpen(false)}
            className="p-2 rounded-xl text-[#6b6b6b] dark:text-neutral-400 hover:bg-gray-50 dark:hover:bg-neutral-800"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <SidebarContent isMobile />
      </aside>

      {/* Main content */}
      <div
        className={`flex-1 min-w-0 flex flex-col min-h-screen transition-all duration-300 ${collapsed ? 'xl:ml-[72px]' : 'xl:ml-72'}`}
      >
        {/* Mobile top bar */}
        <header className="xl:hidden sticky top-0 z-20 bg-white/95 dark:bg-neutral-900/95 backdrop-blur border-b border-gray-200 dark:border-neutral-800 h-14 flex items-center px-4 justify-between">
          <button
            onClick={() => setMobileOpen(true)}
            className="p-2 rounded-xl text-[#6b6b6b] dark:text-neutral-400 hover:bg-gray-50 dark:hover:bg-neutral-800"
          >
            <Menu className="h-5 w-5" />
          </button>
          <button
            onClick={toggleTheme}
            title={isDark ? 'Heller Modus' : 'Dunkler Modus'}
            className="p-2 rounded-xl text-[#6b6b6b] dark:text-neutral-400 hover:bg-gray-50 dark:hover:bg-neutral-800"
          >
            {isDark ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
          </button>
        </header>

        <main className="flex-1 p-5 xl:p-8 max-w-5xl w-full mx-auto">
          {!accessLoading && !hasAccess && profile?.role !== 'admin' && (
            <div className="mb-5 flex items-start gap-3 bg-[#970000]/5 dark:bg-red-500/10 border border-[#970000]/20 dark:border-red-500/40 rounded-xl px-5 py-4">
              <AlertTriangle className="h-5 w-5 text-[#970000] dark:text-red-400 shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-[#111827] dark:text-neutral-100">
                  Dein Zugang ist pausiert
                </p>
                <p className="text-sm text-[#6b6b6b] dark:text-neutral-400 mt-0.5">
                  {hasPendingPayment
                    ? 'Bitte begleiche den offenen Betrag, um wieder auf deine Kurse zugreifen zu können. Zahlungen und Profil bleiben verfügbar.'
                    : 'Für dein Konto ist derzeit kein aktiver Zugang hinterlegt. Bitte wende dich an deinen Lehrer.'}
                </p>
              </div>
              {hasPendingPayment && (
                <button
                  onClick={() => navigate('/dashboard/zahlungen')}
                  className="shrink-0 text-xs font-semibold text-white bg-[#970000] hover:bg-[#7a0000] rounded-lg px-3 py-1.5 transition-colors"
                >
                  Zur Zahlung
                </button>
              )}
            </div>
          )}
          {isWarning && nextDueDate && (
            <div className="mb-5 flex items-start gap-3 bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-900 rounded-xl px-5 py-4">
              <AlertTriangle className="h-5 w-5 text-amber-500 shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-[#111827] dark:text-neutral-100">
                  {nextDueDate < new Date().toISOString().slice(0, 10) ? 'Zahlung überfällig seit' : 'Zahlung fällig am'}{' '}
                  {new Date(nextDueDate + 'T00:00:00').toLocaleDateString('de-DE', { day: '2-digit', month: 'long', year: 'numeric' })}
                </p>
                <p className="text-sm text-[#6b6b6b] dark:text-neutral-400 mt-0.5">
                  Bitte stelle sicher, dass deine Zahlung rechtzeitig eingeht, damit dein Zugang nicht unterbrochen wird.
                </p>
              </div>
              <button
                onClick={() => navigate('/dashboard/zahlungen')}
                className="shrink-0 text-xs font-semibold text-amber-700 dark:text-amber-300 bg-amber-100 dark:bg-amber-900/40 hover:bg-amber-200 dark:hover:bg-amber-900/70 rounded-lg px-3 py-1.5 transition-colors"
              >
                Zur Zahlung
              </button>
            </div>
          )}
          <Outlet />
        </main>
      </div>
    </div>
    </div>
  );
};

export default StudentDashboardLayout;